import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import { useTicketStore } from "@app/Store/ticket-store";
import {
  BookingTicketTypes,
  type BookingTicketType,
} from "@features/Bookings/Constants/ticket-types";
import { TicketDistanceTypes } from "@features/Bookings/Constants/ticket-distance-types";
import { useDate } from "@shared/Services/date-service";

const createTicketSelection = (): TicketSelection => ({
  baseProductId: null,
  variantId: null,
  locationFrom: null,
  locationTo: null,
  date: null,
  selectedSeason: null,
  selectReturnDateLater: false,
});

const createBoatDetails = (): BoatDetails => ({
  systemId: "",
  name: "",
  type: "",
  insuranceCompany: "",
  length: null,
  width: null,
});

export const useSelectionStore = defineStore("selection", () => {
  const dateService = useDate();
  const ticketStore = useTicketStore();
  const temporarySelectedDate: Ref<string | null> = ref(null);
  const customerSelections: Ref<CustomerSelections> = ref({
    departureTicket: createTicketSelection(),
    returnTicket: null,
    boatDetails: createBoatDetails(),
  });

  const setTemporarySelectedDate = (date: Date | string | null): void => {
    temporarySelectedDate.value = dateService.toDateString(date);
  };

  const setBoatDetails = (boatDetails: Partial<BoatDetails>): void => {
    customerSelections.value.boatDetails = {
      ...customerSelections.value.boatDetails,
      ...boatDetails,
    };
  };

  const resetBoatDetails = (): void => {
    customerSelections.value.boatDetails = createBoatDetails();
  };

  const confirmTemporaryDate = (ticketType: BookingTicketType): boolean => {
    if (!temporarySelectedDate.value) {
      return false;
    }

    const ticketSelection = getTicketSelection(ticketType);

    if (!ticketSelection) {
      return false;
    }

    ticketSelection.date = temporarySelectedDate.value;
    return true;
  };

  const getTicketSelection = (ticketType: BookingTicketType): TicketSelection | null => {
    return ticketType === BookingTicketTypes.Return
      ? customerSelections.value.returnTicket
      : customerSelections.value.departureTicket;
  };
  
  const isMiniTrip = (): boolean => {
    if (customerSelections.value.departureTicket?.baseProductId) {
      const ticket = ticketStore.tickets.find((candidate) =>
        candidate.baseProductId === customerSelections.value.departureTicket?.baseProductId ||
        candidate.baseProductSystemId === customerSelections.value.departureTicket?.baseProductId
      );
      return ticket?.distanceType === TicketDistanceTypes.MiniTrip;
    }
    return false;
  }

  const clearTicketChoices = (): void => {
    const selectedTicket = customerSelections.value.departureTicket;

    selectedTicket.baseProductId = null;
    selectedTicket.variantId = null;
    selectedTicket.locationFrom = null;
    selectedTicket.locationTo = null;
    selectedTicket.date = null;
    selectedTicket.selectedSeason = null;
    selectedTicket.selectReturnDateLater = false;
    customerSelections.value.returnTicket = null;
  };

  const clearCustomerSelections = (): void => {
    clearTicketChoices();
    resetBoatDetails();
  };

  return {
    customerSelections,
    temporarySelectedDate,
    setTemporarySelectedDate,
    setBoatDetails,
    resetBoatDetails,
    confirmTemporaryDate,
    getTicketSelection,
    clearTicketChoices,
    clearCustomerSelections,
    isMiniTrip
  };
});
